import { Breakpoint } from '../../hooks/useBounds/getViewport/getViewport';
import { type CSSProperties } from 'react';
import { type Shadows } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    displayL: CSSProperties;
    displayM: CSSProperties;
    displayS: CSSProperties;
    bodyL: CSSProperties;
    bodyM: CSSProperties;
    bodyS: CSSProperties;
    linkM: CSSProperties;
    linkS: CSSProperties;
    caption: CSSProperties;
    captionLink: CSSProperties;
  }

  interface TypographyVariantsOptions {
    displayL?: CSSProperties;
    displayM?: CSSProperties;
    displayS?: CSSProperties;
    titleH1?: CSSProperties;
    titleH2?: CSSProperties;
    titleH3?: CSSProperties;
    titleH4?: CSSProperties;
    bodyL?: CSSProperties;
    bodyM?: CSSProperties;
    linkM?: CSSProperties;
    bodyS?: CSSProperties;
    linkS?: CSSProperties;
    caption?: CSSProperties;
    captionLink?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    displayL: true;
    displayM: true;
    displayS: true;
    titleH1: true;
    titleH2: true;
    titleH3: true;
    titleH4: true;
    bodyL: true;
    bodyM: true;
    linkM: true;
    bodyS: true;
    linkS: true;
    caption: true;
    captionLink: true;
  }
}

export const themeOptions = {
  spacing: [
    0, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48, 52, 56, 60, 64, 68, 72, 76,
  ],
  shape: {
    borderRadius: 1,
    radius4: '4px',
    radius8: '8px',
    radius12: '12px',
    radius16: '16px',
    radius100: '100px',
  },
  transitions: {
    duration: {
      shortest: 100,
      shorter: 150,
      short: 200,
      standard: 300,
      long: 500,
      enteringScreen: 500,
      leavingScreen: 500,
    },
    easing: {
      easeIn: 'cubic-bezier(0.540, -0.600, 0.000, 1.650)',
    },
  },
  typography: {
    textShadow: '0px 2px 2px rgba(0, 0, 0, 0.3)',
    inputShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    fontFamily: 'Suisse Intl, Arial, Helvetica, sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightSemiBold: 600,
    displayL: {
      fontSize: '4rem',
      lineHeight: '1.125',
    },
    displayM: {
      fontSize: '3rem',
      lineHeight: '1.167',
    },
    displayS: {
      fontSize: '2.5rem',
      lineHeight: '1.2',
    },
    titleH1: {
      fontSize: '2.25rem',
      lineHeight: '1.222',
    },
    titleH2: {
      fontSize: '2rem',
      lineHeight: '1.25',
    },
    titleH3: {
      fontSize: '1.5rem',
      lineHeight: '1.333',
    },
    titleH4: {
      fontSize: '1.25rem',
      lineHeight: '1.4',
    },
    bodyL: {
      fontSize: '1.125rem',
      lineHeight: '1.444',
    },
    bodyM: {
      fontSize: '1rem',
      lineHeight: '1.5',
    },
    linkM: {
      fontSize: '1rem',
      lineHeight: '1.5',
    },
    bodyS: {
      fontSize: '0.875rem',
      lineHeight: '1.429',
    },
    linkS: {
      fontSize: '0.875rem',
      lineHeight: '1.429',
    },
    caption: {
      fontFamily: undefined,
      fontWeight: undefined,
      fontSize: '0.75rem',
      lineHeight: '1.333',
    },
    captionLink: {
      fontSize: '0.75rem',
      lineHeight: '1.333',
    },
    // MUI overrides
    h2: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 20,
      lineHeight: '26px',
    },
    body1: {
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: 14,
      lineHeight: 1.3,
    },
    h3: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 16,
      lineHeight: '21px',
    },
    h4: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 14,
      lineHeight: '18px',
    },
    h5: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 12,
      lineHeight: '16px',
    },
    h6: {
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: 12,
      lineHeight: '16px',
    },
    subtitle1: {
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: 12,
      lineHeight: '16px',
    },
    button: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 14,
      lineHeight: '20px',
    },
  },
  /**
   * Note: Please keep these in z-index order
   */
  zIndex: {
    pauseOverlay: 1,
    loadingOverlay: 1,
    nameTag: 2,
    componentOverlay: 2,
    localPreview: 2,
    linkInteractiveFlyout: 2,
    panels: 4,
    drawer: 5,
    // MUI overrides
    mobileStepper: 1000,
    speedDial: 1050,
    fab: 1050,
    appBar: 1100,
    alertBanner: 1200,
    modal: 1300,
    snackbar: 1400,
    popover: 1500,
    toasts: 1500,
    viewModeBanner: 1600,
    tooltip: 1700,
    navigationDrawer: 1800,
  },
  shadows: [
    '0px 0px 0.4px 0px rgba(0, 0, 0, 0.04), 0px 0px 1.59px 0px rgba(0, 0, 0, 0.06), 0px 0px 5px 0px rgba(0, 0, 0, 0.10)',
    '0px 0.16px 0.81px 0px rgba(0, 0, 0, 0.03), 0px 0.77px 3.18px 0px rgba(0, 0, 0, 0.04), 0px 2px 10px 0px rgba(0, 0, 0, 0.07)',
    '0px 0.57px 1.38px 0px rgba(0, 0, 0, 0.04), 0px 2.69px 5.41px 0px rgba(0, 0, 0, 0.05), 0px 7px 17px 0px rgba(0, 0, 0, 0.09)',
    '0px 1.54px 2.52px 2px rgba(0, 0, 0, 0.03), 0px 7.31px 9.87px 2px rgba(0, 0, 0, 0.04), 0px 19px 31px 2px rgba(0, 0, 0, 0.07)',
    '0px 1.95px 4.07px 2px rgba(0, 0, 0, 0.04), 0px 9.24px 15.92px 2px rgba(0, 0, 0, 0.06), 0px 24px 50px 2px rgba(0, 0, 0, 0.10)',
    '0px 3.34px 6.59px 2px rgba(0, 0, 0, 0.05), 0px 15.79px 25.79px 2px rgba(0, 0, 0, 0.09), 0px 41px 81px 2px rgba(0, 0, 0, 0.14)',
    '0px 60px 100px 2px rgba(0, 0, 0, 0.20), 0px 23.111px 31.852px 2px rgba(0, 0, 0, 0.12), 0px 4.889px 8.148px 2px rgba(0, 0, 0, 0.08)',
    '0px 81px 124px 2px rgba(0, 0, 0, 0.30), 0px 31.2px 39.496px 2px rgba(0, 0, 0, 0.18), 0px 6.6px 10.104px 2px rgba(0, 0, 0, 0.12)',
    // MUI expects 25 shadow values (indices 0-24), fill remaining with 'none'
    ...Array(17).fill('none'),
  ] as unknown as Shadows,
  scrollShadow: {
    up: '0px -2px 4px 0px rgba(0, 0, 0, 0.06)',
    down: '0px 2px 4px 0px rgba(0, 0, 0, 0.06)',
  },
  backdropFilter: {
    small: 'blur(10px)',
    medium: 'blur(20px)',
    large: 'blur(30px)',
    xlarge: 'blur(50px)',
    xxlarge: 'blur(74px)',
    max: 'blur(100px)',
  },
  components: {
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '0.875rem',
          [`@media (max-width: ${
            Breakpoint.medium - 1
          }px) and (pointer: coarse)`]: {
            fontSize: '1rem',
          },
        },
      },
    },
  },
};
